/**
 * Photo uploads go straight from the device to object storage on a
 * pre-signed URL; the API only records the metadata afterwards.
 */
import type { AttachAudioBody } from '@klunkers/shared'
import {
  Capture,
  Photo,
  PresignResponse,
  type ALLOWED_IMAGE_TYPES,
  type CreatePhotoBody,
  type UploadContentType,
} from '@klunkers/shared'
import { api, NetworkError } from './http'

export type ImageType = (typeof ALLOWED_IMAGE_TYPES)[number]

export function imageType(contentType: string): ImageType {
  const t = contentType.toLowerCase()
  if (t === 'image/png' || t === 'image/webp' || t === 'image/heic' || t === 'image/heif') return t
  return 'image/jpeg'
}

export async function presign(contentType: UploadContentType, size: number, uploadId?: string) {
  return api('/uploads/presign', {
    method: 'POST',
    body: { content_type: contentType, size, ...(uploadId ? { upload_id: uploadId } : {}) },
    schema: PresignResponse,
  })
}

export async function putObject(url: string, headers: Record<string, string>, blob: Blob) {
  let res: Response
  try {
    res = await fetch(url, { method: 'PUT', headers, body: blob })
  } catch {
    throw new NetworkError()
  }
  if (!res.ok) throw new Error(`Upload failed (${res.status}).`)
}

export async function recordPhoto(body: CreatePhotoBody) {
  return api('/photos', { method: 'POST', body, schema: Photo })
}

/** Upload a photo now (online path) and record it against its owner. */
export async function uploadPhoto(
  blob: Blob,
  owner: Pick<CreatePhotoBody, 'owner_type' | 'owner_id' | 'tag'>,
) {
  const type = imageType(blob.type)
  const signed = await presign(type, blob.size)
  await putObject(signed.upload_url, signed.headers, blob)
  return recordPhoto({ object_key: signed.object_key, content_type: type, ...owner })
}

/** Attach an uploaded voice note to a capture. Idempotent on the object key. */
export async function attachAudio(captureId: string, body: AttachAudioBody) {
  return api(`/captures/${captureId}/audio`, { method: 'POST', body, schema: Capture })
}
